import { Link, useLocation } from "wouter";

/**
 * AlternativeGuides — on a treatment page, states in prose which well-known
 * brand sits in the same technology class and links to the comparison.
 *
 * Patients arrive having heard a brand name, not a modality. Saying "this is
 * the same technology class as Morpheus8" in visible copy is both what they
 * need to read and the signal search engines actually rank — the brand in
 * body text and an internal link, not a hidden keyword.
 *
 * `offered: true` marks a product we actually administer (Sculptra), where an
 * "alternative to" framing would be wrong and a non-affiliation disclaimer
 * would be false.
 *
 * Website palette only (slate #5B7A9E / ivory #FAF7F2 / rose-brown #8B6B6B).
 * The social palette must never appear here.
 */
interface Guide {
  brand: string;
  owner: string;
  sharedTech: string;
  href: string;
  offered?: boolean;
}

const GUIDES: Record<string, { ours: string; guides: Guide[] }> = {
  "/rf-microneedling": {
    ours: "Our RF microneedling",
    guides: [{ brand: "Morpheus8", owner: "InMode", sharedTech: "fractional RF microneedling", href: "/morpheus8-alternative-dallas" }],
  },
  "/dpl-photofacial": {
    ours: "Our IPL photofacial",
    guides: [{ brand: "Sciton BBL", owner: "Sciton", sharedTech: "broadband light", href: "/bbl-alternative-dallas" }],
  },
  "/duovive": {
    ours: "DuoVive",
    guides: [
      { brand: "Fraxel", owner: "Solta Medical", sharedTech: "non-ablative fractional laser", href: "/fraxel-alternative-dallas" },
      { brand: "Clear + Brilliant", owner: "Solta Medical", sharedTech: "the 1927nm fractional wavelength", href: "/clear-brilliant-alternative-dallas" },
    ],
  },
  "/hifu": {
    ours: "Our HIFU",
    guides: [{ brand: "Ultherapy", owner: "Merz Aesthetics", sharedTech: "focused ultrasound", href: "/ultherapy-alternative-dallas" }],
  },
  "/fractional-co2": {
    ours: "Our fractional CO₂ laser",
    guides: [{ brand: "CoolPeel", owner: "Cartessa Aesthetics", sharedTech: "the 10,600nm fractional CO₂ wavelength", href: "/coolpeel-alternative-dallas" }],
  },
  "/renasculpt": {
    ours: "RenaSculpt",
    guides: [{ brand: "Emsculpt NEO", owner: "BTL Industries", sharedTech: "HIFEM with synchronized radiofrequency", href: "/emsculpt-neo-alternative-dallas" }],
  },
  "/pico-laser": {
    ours: "Our PicoLaser",
    guides: [{ brand: "PicoSure", owner: "Cynosure", sharedTech: "picosecond laser treatment, including the 755nm wavelength", href: "/picosure-alternative-dallas" }],
  },
  "/biostimulators": {
    ours: "We administer Sculptra",
    guides: [{ brand: "Sculptra", owner: "Galderma", sharedTech: "poly-L-lactic acid collagen stimulation", href: "/sculptra-alternative-dallas", offered: true }],
  },
};

export default function AlternativeGuides() {
  const [location] = useLocation();
  const entry = GUIDES[location.split("?")[0]];
  if (!entry?.guides.length) return null;

  const { ours, guides } = entry;
  const link = { color: "#5B7A9E", fontWeight: 800, textDecoration: "underline" };

  return (
    <aside
      aria-label="Comparable treatment technologies"
      style={{ background: "#FAF7F2", borderTop: "1px solid #E3DCD2", padding: "1.9rem 1.25rem" }}
    >
      <div
        style={{
          maxWidth: 780,
          margin: "0 auto",
          fontFamily: "'Lato',sans-serif",
          fontSize: ".93rem",
          lineHeight: 1.7,
          color: "#5A544E",
          display: "flex",
          flexDirection: "column",
          gap: ".6rem",
        }}
      >
        <strong style={{ color: "#2C2C2C", fontSize: ".82rem", letterSpacing: ".08em", textTransform: "uppercase" }}>
          Heard of it by another name?
        </strong>
        {guides.map((g) => (
          <p key={g.href} style={{ margin: 0 }}>
            {g.offered ? (
              <>
                {ours}, a {g.sharedTech} treatment from {g.owner}, alongside other biostimulators.{" "}
                <Link href={g.href} style={link}>
                  Compare {g.brand} with the alternatives →
                </Link>
              </>
            ) : (
              <>
                {ours} is the same technology class as <strong style={{ color: "#2C2C2C" }}>{g.brand}®</strong> — both
                use {g.sharedTech}. We are not affiliated with {g.owner} and do not offer {g.brand}; device settings,
                protocols and results differ between systems.{" "}
                <Link href={g.href} style={link}>
                  See how {g.brand} and {ours.replace(/^Our /, "our ")} compare →
                </Link>
              </>
            )}
          </p>
        ))}
      </div>
    </aside>
  );
}
